import React, { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Search, Plus, X as XIcon, Users } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["doctor", "distributor", "retailer", "other"];

/**
 * Reusable Customer-Master picker. Wraps live search + select-from-list +
 * save-as-new. Designed for any order-placement flow that wants to auto-fill
 * the customer's address from a saved master record.
 *
 * Props:
 *   value               currently selected customer (object | null)
 *   onPick(customer)    called when user picks a customer; receives the full
 *                       customer doc OR null when cleared
 *   onSaveAsNew()       optional async callback when user clicks "Save as new"
 *                       (the parent typically calls POST /customers itself
 *                       and then re-feeds value)
 *   showTypeSelector    when true, also renders a small Type dropdown that
 *                       calls onTypeChange (used by MR to switch pricing)
 *   typeValue           current customer_type (when showTypeSelector)
 *   onTypeChange(v)     called when type changes (when showTypeSelector)
 *   compact             render in a tighter, single-row form (for inline use)
 *   dataTestIdPrefix    prefix for data-testids
 */
export default function CustomerMasterPicker({
    value,
    onPick,
    onSaveAsNew,
    showTypeSelector = false,
    typeValue,
    onTypeChange,
    compact = false,
    dataTestIdPrefix = "cmp",
}) {
    const [customers, setCustomers] = useState([]);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const wrapRef = useRef(null);

    // Hydrate the list once
    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/customers");
                setCustomers(data || []);
            } catch (e) {
                if (e.response?.status !== 403) toast.error("Couldn't load customer list");
            } finally { setLoading(false); }
        };
        load();
    }, []);

    // Close suggestions on outside click
    useEffect(() => {
        const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const filtered = customers.filter(c => {
        if (!q.trim()) return true;
        const n = q.toLowerCase();
        return (c.name || "").toLowerCase().includes(n)
            || (c.phone || "").includes(q)
            || (c.city || "").toLowerCase().includes(n)
            || (c.email || "").toLowerCase().includes(n);
    }).slice(0, 30);

    const pick = (c) => { onPick?.(c); setQ(""); setOpen(false); };
    const clear = () => onPick?.(null);

    return (
        <div ref={wrapRef} className={`bg-sky-50/60 border border-sky-200 ${compact ? "p-2" : "p-3"} space-y-2`} data-testid={`${dataTestIdPrefix}-root`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="text-[10px] uppercase tracking-widest text-sky-900 font-semibold inline-flex items-center gap-1.5">
                    <Users className="w-3 h-3" />Customer master {loading && <span className="text-stone-400">· loading…</span>}
                </span>
                {showTypeSelector && (
                    <div className="flex items-center gap-2 text-xs">
                        <Label className="text-[10px] uppercase tracking-widest text-stone-500">Type</Label>
                        <Select value={typeValue || "retailer"} onValueChange={onTypeChange}>
                            <SelectTrigger className="rounded-none border-stone-300 h-7 w-32 text-xs" data-testid={`${dataTestIdPrefix}-type`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </div>

            {value ? (
                <div className="bg-white border border-sky-300 px-3 py-2 flex items-start justify-between gap-2" data-testid={`${dataTestIdPrefix}-selected`}>
                    <div className="text-xs">
                        <div className="font-medium text-stone-900">{value.name}</div>
                        <div className="text-[10px] text-stone-500">{[value.phone, value.city, value.state].filter(Boolean).join(" · ") || "(no phone / address)"}</div>
                    </div>
                    <Button onClick={clear} variant="outline" size="sm" className="rounded-none border-stone-300 h-7 px-2 text-xs" data-testid={`${dataTestIdPrefix}-clear`}>
                        <XIcon className="w-3 h-3 mr-1" />Clear
                    </Button>
                </div>
            ) : (
                <div className="relative">
                    <div className="relative">
                        <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-stone-400" />
                        <Input
                            value={q}
                            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                            onFocus={() => setOpen(true)}
                            placeholder="Search saved customers by name, phone, city or email…"
                            className="rounded-none border-stone-300 h-9 pl-7 text-sm"
                            data-testid={`${dataTestIdPrefix}-search`}
                        />
                    </div>
                    {open && q.trim() && filtered.length > 0 && (
                        <ul className="absolute z-50 left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-stone-300 shadow-sm" data-testid={`${dataTestIdPrefix}-suggestions`}>
                            {filtered.map(c => (
                                <li
                                    key={c.id}
                                    onClick={() => pick(c)}
                                    className="px-3 py-2 hover:bg-sky-50 cursor-pointer text-xs border-b border-stone-100 last:border-b-0"
                                    data-testid={`${dataTestIdPrefix}-item-${c.id}`}
                                >
                                    <div className="font-medium text-stone-900">{c.name} <span className="text-[10px] uppercase tracking-widest text-stone-400 ml-1">{c.customer_type}</span></div>
                                    <div className="text-[10px] text-stone-500">{[c.phone, c.city, c.state].filter(Boolean).join(" · ") || "—"}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                    {open && q.trim() && filtered.length === 0 && (
                        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-stone-300 px-3 py-3 text-xs text-stone-500 flex items-center justify-between gap-2" data-testid={`${dataTestIdPrefix}-no-match`}>
                            <span>No saved customer matches "<strong>{q}</strong>"</span>
                            {onSaveAsNew && (
                                <Button onClick={() => { onSaveAsNew(); setOpen(false); }} size="sm" className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50 h-7 text-xs" data-testid={`${dataTestIdPrefix}-save-new`}>
                                    <Plus className="w-3 h-3 mr-1" />Save this address as new
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// Expose helper so SubscribeButton / others can re-use without prop-drilling
export const CUSTOMER_TYPES = TYPES;
